/**
 * Self Connection Exercises - Library of exercises for deepening self-relationship
 * Now integrated with progress tracking and achievements
 */

import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Heart, Brain, Shield, Compass, Eye, 
  Sparkles, Target, Lightbulb,
  ChevronRight, CheckCircle2, Clock,
  Star, Leaf, Trophy
} from 'lucide-react';
import { SelfConnectionExerciseModal } from './SelfConnectionExerciseModal';
import { useRelationalProgress } from '@/hooks/useRelationalProgress';

interface Exercise {
  id: string;
  title: string;
  description: string;
  duration: string;
  category: 'awareness' | 'compassion' | 'boundaries' | 'values' | 'inner-dialogue';
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  icon: React.ElementType;
  benefits: string[];
}

const SELF_CONNECTION_EXERCISES: Exercise[] = [
  {
    id: 'body-scan-awareness',
    title: 'Body Scan Awareness',
    description: 'Tune into your physical sensations to understand what your body is telling you right now.',
    duration: '10 min',
    category: 'awareness',
    difficulty: 'beginner',
    icon: Eye,
    benefits: ['Increased body awareness', 'Stress recognition', 'Emotional intelligence'],
  },
  {
    id: 'emotional-check-in',
    title: 'Emotional Check-In',
    description: 'Name and explore your current emotional state without judgment.',
    duration: '5 min',
    category: 'awareness',
    difficulty: 'beginner',
    icon: Heart,
    benefits: ['Emotional literacy', 'Self-understanding', 'Present moment awareness'],
  },
  {
    id: 'values-clarification',
    title: 'Values Clarification',
    description: 'Identify what truly matters to you and how aligned your life is with those values.',
    duration: '20 min',
    category: 'values',
    difficulty: 'intermediate',
    icon: Compass,
    benefits: ['Life direction clarity', 'Decision-making confidence', 'Authentic living'],
  },
  {
    id: 'self-compassion-break',
    title: 'Self-Compassion Break',
    description: 'A three-step practice to offer yourself kindness during difficult moments.',
    duration: '5 min',
    category: 'compassion',
    difficulty: 'beginner',
    icon: Sparkles,
    benefits: ['Reduced self-criticism', 'Emotional resilience', 'Inner peace'],
  },
  {
    id: 'inner-child-dialogue',
    title: 'Inner Child Dialogue',
    description: 'Connect with and comfort your younger self through guided visualisation.',
    duration: '15 min',
    category: 'compassion',
    difficulty: 'intermediate',
    icon: Heart,
    benefits: ['Healing past wounds', 'Self-acceptance', 'Emotional integration'],
  },
  {
    id: 'loving-kindness-self',
    title: 'Loving-Kindness for Self',
    description: 'Direct phrases of goodwill and care toward yourself.',
    duration: '10 min',
    category: 'compassion',
    difficulty: 'beginner',
    icon: Heart,
    benefits: ['Increased self-love', 'Reduced anxiety', 'Greater wellbeing'],
  },
  {
    id: 'boundary-awareness',
    title: 'Boundary Awareness',
    description: 'Explore where you need firmer boundaries and where you might be too rigid.',
    duration: '15 min',
    category: 'boundaries',
    difficulty: 'intermediate',
    icon: Shield,
    benefits: ['Healthier relationships', 'Self-respect', 'Energy protection'],
  },
  {
    id: 'saying-no-practice',
    title: 'Saying No Practice',
    description: 'Build the muscle of declining requests that do not serve you.',
    duration: '10 min',
    category: 'boundaries',
    difficulty: 'intermediate',
    icon: Target,
    benefits: ['Assertiveness', 'Time management', 'Self-advocacy'],
  },
  {
    id: 'inner-critic-dialogue',
    title: 'Inner Critic Dialogue',
    description: 'Identify your inner critic voice and learn to respond with wisdom.',
    duration: '15 min',
    category: 'inner-dialogue',
    difficulty: 'intermediate',
    icon: Brain,
    benefits: ['Reduced negative self-talk', 'Mental clarity', 'Self-acceptance'],
  },
  {
    id: 'future-self-letter',
    title: 'Letter from Future Self',
    description: 'Receive wisdom and encouragement from your wiser future self.',
    duration: '20 min',
    category: 'inner-dialogue',
    difficulty: 'advanced',
    icon: Lightbulb,
    benefits: ['Vision clarity', 'Hope', 'Self-guidance'],
  },
  {
    id: 'needs-inventory',
    title: 'Needs Inventory',
    description: 'Identify your core emotional and physical needs and how well they are being met.',
    duration: '15 min',
    category: 'values',
    difficulty: 'beginner',
    icon: Leaf,
    benefits: ['Self-understanding', 'Need fulfillment', 'Life satisfaction'],
  },
  {
    id: 'authentic-self-exploration',
    title: 'Authentic Self Exploration',
    description: 'Discover who you are beneath social roles and expectations.',
    duration: '25 min',
    category: 'values',
    difficulty: 'advanced',
    icon: Star,
    benefits: ['Self-discovery', 'Authenticity', 'Inner freedom'],
  },
];

const CATEGORY_INFO = {
  awareness: { label: 'Self-Awareness', color: 'bg-accent/10 text-accent', icon: Eye },
  compassion: { label: 'Self-Compassion', color: 'bg-secondary/10 text-secondary', icon: Heart },
  boundaries: { label: 'Boundaries', color: 'bg-primary/10 text-primary', icon: Shield },
  values: { label: 'Values & Needs', color: 'bg-muted/20 text-muted', icon: Compass },
  'inner-dialogue': { label: 'Inner Dialogue', color: 'bg-accent/10 text-accent', icon: Brain },
};

const DIFFICULTY_COLORS = {
  beginner: 'bg-muted/20 text-muted',
  intermediate: 'bg-primary/10 text-primary',
  advanced: 'bg-secondary/10 text-secondary',
};

export function SelfConnectionExercises() {
  const [selectedExercise, setSelectedExercise] = useState<Exercise | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const { 
    completeExercise, 
    getCompletedExerciseIds, 
    getStats,
    isLoading 
  } = useRelationalProgress();

  const completedIds = getCompletedExerciseIds();
  const stats = getStats();

  const filteredExercises = categoryFilter
    ? SELF_CONNECTION_EXERCISES.filter(e => e.category === categoryFilter)
    : SELF_CONNECTION_EXERCISES;

  const completionRate = (completedIds.size / SELF_CONNECTION_EXERCISES.length) * 100;

  const handleExerciseComplete = async (responses: Record<number, string>) => {
    if (!selectedExercise) return;
    
    // Convert responses to string keys
    const stringResponses: Record<string, string> = {};
    Object.entries(responses).forEach(([key, value]) => {
      stringResponses[`step_${key}`] = value;
    });

    await completeExercise(
      selectedExercise.id,
      'self-connection',
      stringResponses
    );
    setSelectedExercise(null);
  };

  return (
    <div className="space-y-6">
      {/* Progress Overview */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 border-primary/20">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-foreground">Your Self-Connection Journey</h3>
              <p className="text-sm text-muted-foreground">
                {completedIds.size} of {SELF_CONNECTION_EXERCISES.length} exercises completed
              </p>
            </div>
            <div className="text-right flex items-center gap-3">
              <div className="flex items-center gap-1 text-primary">
                <Trophy className="w-4 h-4" />
                <span className="font-semibold">{stats.totalPoints}</span>
              </div>
              <span className="text-2xl font-light text-primary">{Math.round(completionRate)}%</span>
            </div>
          </div>
          <Progress value={completionRate} className="h-2" />
        </CardContent>
      </Card>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={categoryFilter === null ? "default" : "outline"}
          size="sm"
          onClick={() => setCategoryFilter(null)}
        >
          All
        </Button>
        {Object.entries(CATEGORY_INFO).map(([key, info]) => {
          const Icon = info.icon;
          return (
            <Button
              key={key}
              variant={categoryFilter === key ? "default" : "outline"}
              size="sm"
              onClick={() => setCategoryFilter(key)}
              className="gap-1"
            >
              <Icon className="w-3 h-3" />
              {info.label}
            </Button>
          );
        })}
      </div>

      {/* Exercises Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {filteredExercises.map((exercise) => {
          const Icon = exercise.icon;
          const categoryInfo = CATEGORY_INFO[exercise.category];
          const isCompleted = completedIds.has(exercise.id);

          return (
            <Card
              key={exercise.id}
              className={`cursor-pointer transition-all hover:shadow-md hover:border-primary/30 ${
                isCompleted ? 'bg-muted/10 border-accent/30' : ''
              }`}
              onClick={() => setSelectedExercise(exercise)}
            >
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <div className={`p-2 rounded-lg ${categoryInfo.color}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-sm truncate">{exercise.title}</h4>
                      {isCompleted && (
                        <CheckCircle2 className="w-4 h-4 text-accent flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground line-clamp-2 mb-2">
                      {exercise.description}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="secondary" className="text-xs gap-1">
                        <Clock className="w-3 h-3" />
                        {exercise.duration}
                      </Badge>
                      <Badge className={`text-xs ${DIFFICULTY_COLORS[exercise.difficulty]}`}>
                        {exercise.difficulty}
                      </Badge>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Exercise Modal */}
      {selectedExercise && (
        <SelfConnectionExerciseModal
          exercise={selectedExercise}
          open={!!selectedExercise}
          onOpenChange={(open) => !open && setSelectedExercise(null)}
          onComplete={handleExerciseComplete}
          isCompleted={completedIds.has(selectedExercise.id)}
        />
      )}
    </div>
  );
}
